import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Eye, Send, Code, Layout, AlertCircle,
  CheckCircle, Plus, Trash2, RotateCcw, History, X, Variable
} from 'lucide-react';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';

// Custom styles for the email editor
const editorStyles = `
  #blocks-panel .gjs-block {
    width: 100%;
    min-height: 45px;
    margin-bottom: 8px;
    padding: 8px;
    border-radius: 4px;
    cursor: grab;
  }
  #blocks-panel .gjs-block:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
  .gjs-cv-canvas {
    background-color: #e5e5e5;
  }
`;
import mjmlPlugin from 'grapesjs-mjml';
import { emailTemplatesAPI } from '../../services/api';

const CATEGORIES = [
  { value: 'authentication', label: 'Authentication' },
  { value: 'notification', label: 'Notification' },
  { value: 'billing', label: 'Billing' },
  { value: 'marketing', label: 'Marketing' },
];

// Common variables available for all templates (from Portal Settings + user context)
const COMMON_VARIABLES = [
  { name: 'firstName', description: 'Recipient first name' },
  { name: 'lastName', description: 'Recipient last name' },
  { name: 'email', description: 'Recipient email' },
  { name: 'companyName', description: 'Your company name (from Portal Settings)' },
  { name: 'supportEmail', description: 'Support email (from Portal Settings)' },
  { name: 'supportPhone', description: 'Support phone (from Portal Settings)' },
  { name: 'portalUrl', description: 'Portal URL' },
  { name: 'year', description: 'Current year' },
];

const DEFAULT_MJML = `<mjml>
  <mj-head>
    <mj-title>{{subject}}</mj-title>
    <mj-preview>{{preview_text}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, sans-serif" />
      <mj-text font-size="14px" color="#333333" line-height="1.5" />
      <mj-button background-color="#2563eb" color="#ffffff" font-size="14px" border-radius="4px" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#f4f4f5">
    <mj-section background-color="#ffffff" padding="20px">
      <mj-column>
        <mj-image src="https://via.placeholder.com/150x50?text=Logo" alt="Logo" width="150px" />
      </mj-column>
    </mj-section>
    <mj-section background-color="#ffffff" padding="20px">
      <mj-column>
        <mj-text font-size="24px" font-weight="bold" color="#18181b">
          Hello {{firstName}}!
        </mj-text>
        <mj-text>
          Your email content goes here. Use {{variable_name}} syntax for dynamic content.
        </mj-text>
        <mj-button href="{{action_url}}">
          Click Here
        </mj-button>
      </mj-column>
    </mj-section>
    <mj-section padding="20px">
      <mj-column>
        <mj-text font-size="12px" color="#71717a" align="center">
          &copy; {{year}} {{companyName}}. All rights reserved.
        </mj-text>
        <mj-text font-size="12px" color="#71717a" align="center">
          {{supportEmail}} | {{supportPhone}}
        </mj-text>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;

export default function EmailTemplateEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const grapesEditorRef = useRef(null);
  const isNewTemplate = id === 'new';

  // Template data
  const [template, setTemplate] = useState({
    slug: '',
    name: '',
    description: '',
    subject: '',
    category: 'notification',
    mjml_source: DEFAULT_MJML,
    text_version: '',
    variables: [],
    is_active: true,
  });

  // UI state
  const [loading, setLoading] = useState(!isNewTemplate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'code'
  const [mjmlCode, setMjmlCode] = useState(DEFAULT_MJML);
  const [validationErrors, setValidationErrors] = useState([]);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewVariables] = useState({});

  // Version history state
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Test email state
  const [showTestEmail, setShowTestEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);

  // New variable state
  const [newVariable, setNewVariable] = useState({ name: '', description: '', defaultValue: '' });

  // Load template data
  useEffect(() => {
    if (!isNewTemplate) {
      loadTemplate();
    }
  }, [id]);

  const loadTemplate = async () => {
    try {
      const response = await emailTemplatesAPI.get(id);
      const t = response.data.template;
      setTemplate({
        slug: t.slug,
        name: t.name,
        description: t.description || '',
        subject: t.subject,
        category: t.category || 'notification',
        mjml_source: t.mjml_source,
        text_version: t.text_version || '',
        variables: t.variables || [],
        is_active: t.is_active,
      });
      setMjmlCode(t.mjml_source);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  // Initialize GrapesJS editor
  useEffect(() => {
    if (editorMode === 'visual' && editorRef.current && !loading) {
      // Destroy existing editor
      if (grapesEditorRef.current) {
        grapesEditorRef.current.destroy();
      }

      const editor = grapesjs.init({
        container: editorRef.current,
        height: '600px',
        width: 'auto',
        plugins: [mjmlPlugin],
        pluginsOpts: {
          [mjmlPlugin]: {
            resetBlocks: true,
            resetDevices: true,
            resetStyleManager: true,
          },
        },
        storageManager: false,
        fromElement: false,
        // Device manager for responsive preview
        deviceManager: {
          devices: [
            { name: 'Desktop', width: '' },
            { name: 'Mobile', width: '320px', widthMedia: '480px' },
          ],
        },
        // Block manager configuration - append blocks to the side panel
        blockManager: {
          appendTo: '#blocks-panel',
        },
        // Canvas styles
        canvas: {
          styles: [
            'https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap',
          ],
        },
      });

      // Load the MJML content after editor is ready
      editor.on('load', () => {
        // Set the MJML content
        editor.setComponents(mjmlCode);
      });

      // Add custom commands
      editor.Commands.add('get-mjml', {
        run: (ed) => {
          return ed.getHtml();
        },
      });

      grapesEditorRef.current = editor;
    }

    return () => {
      if (grapesEditorRef.current && editorMode === 'visual') {
        // Don't destroy on mode switch, just when unmounting
      }
    };
  }, [editorMode, loading]);

  // Sync MJML code when switching modes
  const handleModeSwitch = (mode) => {
    if (mode === 'code' && grapesEditorRef.current) {
      // Get MJML from visual editor
      const mjml = grapesEditorRef.current.getHtml();
      setMjmlCode(mjml);
    }
    setEditorMode(mode);
  };

  // Validate MJML
  const validateMjml = async (mjml) => {
    try {
      const response = await emailTemplatesAPI.validate(mjml);
      setValidationErrors(response.data.errors || []);
      return response.data.valid;
    } catch (err) {
      setValidationErrors([{ message: 'Validation failed' }]);
      return false;
    }
  };

  // Save template
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Get MJML source
      let mjmlSource = mjmlCode;
      if (editorMode === 'visual' && grapesEditorRef.current) {
        mjmlSource = grapesEditorRef.current.getHtml();
      }

      // Validate
      const isValid = await validateMjml(mjmlSource);
      if (!isValid) {
        setError('MJML validation failed. Please fix the errors before saving.');
        setSaving(false);
        return;
      }

      const data = {
        ...template,
        mjml_source: mjmlSource,
      };

      if (isNewTemplate) {
        const response = await emailTemplatesAPI.create(data);
        setSuccess('Template created successfully!');
        navigate(`/admin/email-templates/${response.data.template.id}`, { replace: true });
      } else {
        await emailTemplatesAPI.update(id, data);
        setSuccess('Template saved successfully!');
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Preview template
  const handlePreview = async () => {
    setShowPreview(true);
    setPreviewLoading(true);

    try {
      // Get current MJML
      let mjmlSource = mjmlCode;
      if (editorMode === 'visual' && grapesEditorRef.current) {
        mjmlSource = grapesEditorRef.current.getHtml();
      }

      // Build variables with defaults
      const vars = { ...previewVariables };
      template.variables.forEach((v) => {
        if (!vars[v.name] && v.defaultValue) {
          vars[v.name] = v.defaultValue;
        }
      });

      if (isNewTemplate) {
        // Can't preview without saving first - just compile MJML
        const response = await emailTemplatesAPI.validate(mjmlSource);
        setPreviewHtml(response.data.html || '<div>Preview not available for new templates</div>');
      } else {
        const response = await emailTemplatesAPI.preview(id, vars);
        setPreviewHtml(response.data.html);
      }
    } catch (err) {
      setPreviewHtml(`<div style="padding: 20px; color: red;">Failed to load preview: ${err.message}</div>`);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Load version history
  const handleShowVersions = async () => {
    if (isNewTemplate) return;

    setShowVersions(true);
    setVersionsLoading(true);

    try {
      const response = await emailTemplatesAPI.getVersions(id);
      setVersions(response.data.versions);
    } catch (err) {
      setError('Failed to load version history');
    } finally {
      setVersionsLoading(false);
    }
  };

  // Restore version
  const handleRestoreVersion = async (versionId) => {
    try {
      await emailTemplatesAPI.restoreVersion(id, versionId);
      setShowVersions(false);
      await loadTemplate();
      setSuccess('Version restored successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore version');
    }
  };

  // Send test email
  const handleSendTest = async () => {
    if (!testEmail) return;

    setTestSending(true);

    try {
      await emailTemplatesAPI.sendTest(id, testEmail, previewVariables);
      setSuccess(`Test email sent to ${testEmail}`);
      setShowTestEmail(false);
      setTestEmail('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setTestSending(false);
    }
  };

  // Add variable
  const handleAddVariable = () => {
    if (!newVariable.name.trim()) return;

    setTemplate((prev) => ({
      ...prev,
      variables: [...prev.variables, { ...newVariable }],
    }));
    setNewVariable({ name: '', description: '', defaultValue: '' });
  };

  // Remove variable
  const handleRemoveVariable = (index) => {
    setTemplate((prev) => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index),
    }));
  };

  // Insert variable into code
  const insertVariable = (variableName) => {
    if (editorMode === 'code') {
      const textarea = document.getElementById('mjml-code');
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = `{{${variableName}}}`;
        const newCode = mjmlCode.slice(0, start) + text + mjmlCode.slice(end);
        setMjmlCode(newCode);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Editor Styles */}
      <style>{editorStyles}</style>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/email-templates')}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg dark:text-neutral-300"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">
              {isNewTemplate ? 'Create Template' : 'Edit Template'}
            </h1>
            {!isNewTemplate && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 font-mono">{template.slug}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNewTemplate && (
            <>
              <button
                onClick={handleShowVersions}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              >
                <History className="w-4 h-4" />
                History
              </button>
              <button
                onClick={() => setShowTestEmail(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              >
                <Send className="w-4 h-4" />
                Test
              </button>
            </>
          )}
          <button
            onClick={handlePreview}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3 text-green-700 dark:text-green-300">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <h4 className="font-medium text-yellow-800 dark:text-yellow-300 mb-2">MJML Validation Warnings</h4>
          <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-400">
            {validationErrors.map((err, i) => (
              <li key={i}>{err.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Editor Panel */}
        <div className="lg:col-span-3 space-y-4">
          {/* Editor Mode Toggle */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-2 flex gap-2">
            <button
              onClick={() => handleModeSwitch('visual')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                editorMode === 'visual'
                  ? 'bg-primary-500 text-white'
                  : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
              }`}
            >
              <Layout className="w-4 h-4" />
              Visual Editor
            </button>
            <button
              onClick={() => handleModeSwitch('code')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                editorMode === 'code'
                  ? 'bg-primary-500 text-white'
                  : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
              }`}
            >
              <Code className="w-4 h-4" />
              Code View
            </button>
          </div>

          {/* Editor */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            {editorMode === 'visual' ? (
              <div className="flex">
                {/* Blocks Panel */}
                <div
                  id="blocks-panel"
                  className="w-48 bg-neutral-50 dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-700 p-2 overflow-y-auto max-h-[600px]"
                  style={{ minWidth: '180px' }}
                >
                  <h4 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2 px-1">
                    Drag to add
                  </h4>
                </div>
                {/* Canvas */}
                <div ref={editorRef} className="flex-1 min-h-[600px]" />
              </div>
            ) : (
              <textarea
                id="mjml-code"
                value={mjmlCode}
                onChange={(e) => setMjmlCode(e.target.value)}
                className="w-full h-[600px] p-4 font-mono text-sm border-0 focus:ring-0 resize-none bg-white dark:bg-neutral-800 dark:text-white"
                spellCheck={false}
              />
            )}
          </div>
        </div>

        {/* Settings Panel */}
        <div className="space-y-4">
          {/* Template Info */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <h3 className="font-medium text-neutral-900 dark:text-white mb-4">Template Info</h3>
            <div className="space-y-4">
              {isNewTemplate && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Slug *
                  </label>
                  <input
                    type="text"
                    value={template.slug}
                    onChange={(e) => setTemplate({ ...template, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
                    placeholder="template_slug"
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono bg-white dark:bg-neutral-700 dark:text-white"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={template.name}
                  onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                  placeholder="Template Name"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Subject *
                </label>
                <input
                  type="text"
                  value={template.subject}
                  onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
                  placeholder="Email Subject"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Supports variables: {"{{first_name}}"}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Category
                </label>
                <select
                  value={template.category}
                  onChange={(e) => setTemplate({ ...template, category: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Description
                </label>
                <textarea
                  value={template.description}
                  onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                  rows={2}
                  placeholder="Brief description..."
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={template.is_active}
                  onChange={(e) => setTemplate({ ...template, is_active: e.target.checked })}
                  className="rounded border-neutral-300 dark:border-neutral-600 text-primary-500 focus:ring-primary-500 dark:bg-neutral-700"
                />
                <label htmlFor="is_active" className="text-sm text-neutral-700 dark:text-neutral-300">
                  Active
                </label>
              </div>
            </div>
          </div>

          {/* Common Variables (from Portal Settings) */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <h3 className="font-medium text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
              <Variable className="w-4 h-4" />
              Available Variables
            </h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
              Click to copy. Values from Portal Settings are automatically filled in.
            </p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {COMMON_VARIABLES.map((v) => (
                <div
                  key={v.name}
                  className="flex items-center justify-between gap-2 p-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 rounded text-sm cursor-pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(`{{${v.name}}}`);
                  }}
                  title="Click to copy"
                >
                  <code className="font-mono text-xs text-primary-600 dark:text-primary-400">
                    {`{{${v.name}}}`}
                  </code>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                    {v.description}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Variables */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <h3 className="font-medium text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <Variable className="w-4 h-4" />
              Custom Variables
            </h3>
            <div className="space-y-2 mb-4">
              {template.variables.map((v, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 p-2 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg text-sm"
                >
                  <button
                    onClick={() => insertVariable(v.name)}
                    className="font-mono text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300"
                    title="Click to insert"
                  >
                    {`{{${v.name}}}`}
                  </button>
                  <span className="flex-1 text-neutral-500 dark:text-neutral-400 truncate text-xs">
                    {v.description}
                  </span>
                  <button
                    onClick={() => handleRemoveVariable(i)}
                    className="text-neutral-400 hover:text-red-500 dark:hover:text-red-400"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {template.variables.length === 0 && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-2">No variables defined</p>
              )}
            </div>
            <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4 space-y-2">
              <input
                type="text"
                value={newVariable.name}
                onChange={(e) => setNewVariable({ ...newVariable, name: e.target.value.replace(/[^a-z0-9_]/gi, '') })}
                placeholder="variable_name"
                className="w-full px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded text-sm font-mono bg-white dark:bg-neutral-700 dark:text-white"
              />
              <input
                type="text"
                value={newVariable.description}
                onChange={(e) => setNewVariable({ ...newVariable, description: e.target.value })}
                placeholder="Description"
                className="w-full px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded text-sm bg-white dark:bg-neutral-700 dark:text-white"
              />
              <input
                type="text"
                value={newVariable.defaultValue}
                onChange={(e) => setNewVariable({ ...newVariable, defaultValue: e.target.value })}
                placeholder="Default value"
                className="w-full px-2 py-1 border border-neutral-300 dark:border-neutral-600 rounded text-sm bg-white dark:bg-neutral-700 dark:text-white"
              />
              <button
                onClick={handleAddVariable}
                disabled={!newVariable.name.trim()}
                className="w-full flex items-center justify-center gap-1 px-2 py-1 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded disabled:opacity-50"
              >
                <Plus className="w-3 h-3" />
                Add Variable
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowPreview(false)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Email Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              >
                <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-neutral-100 dark:bg-neutral-900">
              {previewLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden max-w-2xl mx-auto">
                  <iframe
                    srcDoc={previewHtml}
                    title="Email Preview"
                    className="w-full h-[600px] border-0"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {showVersions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowVersions(false)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Version History</h3>
              <button
                onClick={() => setShowVersions(false)}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              >
                <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              {versionsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : versions.length === 0 ? (
                <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
                  <History className="w-8 h-8 mx-auto mb-2 text-neutral-400 dark:text-neutral-500" />
                  No version history available
                </div>
              ) : (
                <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                  {versions.map((version) => (
                    <div key={version.id} className="p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-neutral-900 dark:text-white">Version {version.version_number}</p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          {new Date(version.created_at).toLocaleString()}
                          {version.created_by_email && ` by ${version.created_by_email}`}
                        </p>
                        {version.change_reason && (
                          <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-1">{version.change_reason}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRestoreVersion(version.id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Restore
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowTestEmail(false)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Send Test Email</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowTestEmail(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSendTest}
                disabled={testSending || !testEmail}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
              >
                {testSending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
